import type { RealtimeAnalyticsResponse } from '@shared/api-client';
import { ArrowRight, CircleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import { formatRelative } from '@/lib/utils';
import { LivePulse } from './live-pulse';

export const LiveActivity = ({
  projectId,
  realtime,
  isError,
}: {
  projectId: string;
  realtime: RealtimeAnalyticsResponse | undefined;
  isError: boolean;
}) => {
  // Never substitute a historical summary or a synthetic zero for live data.
  const label = isError
    ? 'Live unavailable'
    : realtime
      ? `${realtime.summary.currentVisitors} active now`
      : 'Loading live…';

  return (
    <HelpTooltip
      key={projectId}
      label={`${label} — live activity`}
      contentRole="dialog"
      className={`h-9 w-auto gap-2 px-2 text-sm font-medium whitespace-nowrap ${isError ? 'text-critical' : 'text-ink'}`}
      icon={
        <>
          {isError ? (
            <CircleAlert aria-hidden="true" className="size-3.5" />
          ) : (
            <LivePulse
              active={Boolean(realtime && realtime.summary.currentVisitors > 0)}
            />
          )}
          <span>{label}</span>
        </>
      }
    >
      {isError ? (
        <p>Live data could not be refreshed. Retrying automatically.</p>
      ) : (
        <p>
          Visitors active in the last {realtime?.activeWindowMinutes ?? 5}{' '}
          minutes. Independent of the selected date range.
        </p>
      )}
      {realtime ? (
        <p className="mt-2 text-ink-muted">
          {realtime.summary.lastEventAt
            ? `Last event ${formatRelative(realtime.summary.lastEventAt)}`
            : 'No recent events'}
        </p>
      ) : null}
      <Link
        to={`/projects/${encodeURIComponent(projectId)}/realtime`}
        className="mt-3 inline-flex min-h-8 items-center gap-1.5 font-medium text-accent hover:text-accent-hover"
      >
        View realtime <ArrowRight aria-hidden="true" className="size-3.5" />
      </Link>
    </HelpTooltip>
  );
};
